import React, { useState } from 'react'
import { useHistory, Redirect } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Form, Input, message } from 'antd'
import { Mail, Lock, ArrowRight, Eye, EyeOff, Clock, DollarSign } from 'lucide-react'
import { AUTH } from '../../utils/apis'
import { authToken, loginUser, setNotificationFlag } from '../../Redux/actions/authActions'
import './LoginPage.css'
import paths from '../../Config/paths'
import { secureApi } from '../../Config/api'
import { getFcmToken } from '../../Config/firebase'

const Login = () => {
  const user = useSelector(state => state.authReducer.user)
  const dispatch = useDispatch()
  const history = useHistory()

  const [passwordVisible, setPasswordVisible] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  if (user) {
    return <Redirect to={paths?.HOME} />
  }

  const handleLogin = async () => {
    try {
      setLoading(true)
      setError('')

      if (!email || !password) {
        message.error('Please provide all fields')
        return
      }

      const fcmToken = await getFcmToken()
      // console.log('FCM Token:', fcmToken)
      const response = await secureApi?.post(AUTH?.LOGIN, { email, password, fcmToken })
      // console.log('Login Response:', response)
      const token = response?.encodedToken
      if (response) {
        const userData = response?.user
        dispatch(loginUser(userData, token))
        dispatch(authToken(token))
        dispatch(setNotificationFlag(true))
        history.push(paths?.HOME)
      }
      if (response?.success === false) {
        message.error(response?.message)
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || 'Something went wrong. Please try again.'
      setError(errorMessage)
      message.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const forgotPassword = () => {
    history.push(paths?.FORGOT_PASSWORD)
  }

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter') {
      handleLogin()
    }
  }

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible)
  }

  return (
    <div className="login-container">
      <div className="left-section">
        <div className="left-content">
          <div className="logo-section">
            <div className="logo-icon">
              <span className="logo-letter">T</span>
            </div>
            <div className="logo-text">
              <div className="company-name">Team</div>
              <div className="company-subtitle">MANAGEMENT</div>
            </div>
          </div>

          <div className="welcome-section">
            <h1 className="main-heading">
              Welcome to <br />
              <span className="dashboard-text">Team Management Dashboard</span>
            </h1>
            <p className="description">
              Track your daily work hours and expenses in one place. <br />
              Simple, efficient and fully managed for employees and admins.
            </p>
          </div>

          <div className="features-section">
            <div className="feature-card">
              <div className="feature-icon analytics-icon">
                <Clock size={24} />
              </div>
              <div className="feature-content">
                <h3 className="feature-title">Work Hours</h3>
                <p className="feature-description">
                  Log and monitor daily hours
                </p>
              </div>
            </div>

            <div className="feature-card">
              <div className="feature-icon security-icon">
                <DollarSign size={24} />
              </div>
              <div className="feature-content">
                <h3 className="feature-title">Expenses</h3>
                <p className="feature-description">
                  Record travel, meals & extra costs
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="right-section">
        <div className="login-form-container">
          <div className="form-header">
            <h2 className="form-title">Welcome back</h2>
            <p className="form-subtitle">Sign in to access your Team Management Dashboard</p>
          </div>

          <Form layout="vertical" className="login-form" onKeyPress={handleKeyPress}>
            <Form.Item
              label="Email"
              name="email"
              validateTrigger={['onSubmit']}
              rules={[{ required: true, message: 'Please enter your Email' }]}
              className="input-group"
            >
              <div className="input-wrapper">
                <Mail className="input-icon" size={20} />
                <Input
                  type="email"
                  placeholder="Enter Your Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-input"
                />
              </div>
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: 'Please Enter Your Password' },
                { min: 8, message: 'Password must be at least 8 characters' }
              ]}
              className="input-group"
            >
              <div className="input-wrapper">
                <Lock className="input-icon" size={20} />
                <Input
                  type={passwordVisible ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input"
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="password-toggle"
                >
                  {passwordVisible ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </Form.Item>

            <Form.Item>
              <button
                type="button"
                onClick={handleLogin}
                disabled={loading}
                className={`submit-button ${loading ? 'loading' : ''}`}
              >
                {loading ? 'Signing in...' : (
                  <>
                    Sign in
                    <ArrowRight size={20} />
                  </>
                )}
              </button>
            </Form.Item>
          </Form>

          <div className="remember-me">
            <p className="forgot-password-text" onClick={forgotPassword}>
              Forgot password?
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login